'use client';

import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { Venue, VenueBookedTimeSlot } from '../../../shared/models';
import { HorizontalFeaturedCard } from './horizontal_featured_card';
import { getVenueById } from '../lib/firebase/venues';


import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

interface SimilarVenuesProps {
    currentVenue: Venue;
    venueBookedTimeSlots: Record<string, VenueBookedTimeSlot[]>;
    attributeIcons: Record<string, string>;
    containerWidth?: 'full' | 'contained';
}

const SimilarVenues = ({
    currentVenue,
    venueBookedTimeSlots,
    attributeIcons,
    containerWidth = 'full'
}: SimilarVenuesProps) => {
    const [similarVenues, setSimilarVenues] = useState<Venue[]>([]);

    useEffect(() => {
        const fetchSimilarVenues = async () => {
            if (!currentVenue.SimilarVenueIds || currentVenue.SimilarVenueIds.length === 0) {
                setSimilarVenues([]);
                return;
            }

            const similarVenuesPromises = currentVenue.SimilarVenueIds.map(id => getVenueById(id));
            const venues = await Promise.all(similarVenuesPromises);

            // Filter out null values and current venue
            setSimilarVenues(venues.filter(venue =>
                venue !== null &&
                venue.ID !== currentVenue.ID
            ) as Venue[]);
        };

        fetchSimilarVenues();
    }, [currentVenue]);

    if (!similarVenues.length) return null;
    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: containerWidth === 'contained' ? '1200px' : '100%',
                margin: containerWidth === 'contained' ? '0 auto' : undefined,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                padding: '32px 0 32px 32px', // Remove right padding
                marginTop: '40px',
                marginBottom: '40px',
                boxShadow: '0 8px 24px rgba(240, 209, 255, 0.15), 0 12px 32px rgba(0,0,0,0.2)',
                border: '2px solid rgba(240, 209, 255, 0.1)',
                backdropFilter: 'blur(8px)',
            }}
        >
            <Typography
                variant="h2"
                sx={{
                    color: '#F0D1FF', // Using the accent color from globals.css
                    fontFamily: 'Inter', // Using Sans font from globals.css
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                    fontWeight: '600',
                    mb: 3,
                    letterSpacing: '-0.02em', // Airbnb-style typography
                    paddingRight: '32px' // Add right padding to title only
                }}
            >
                Similar places with instant booking
            </Typography>
            <Box sx={{ position: 'relative' }}>
                <Swiper
                    modules={[Pagination, Navigation]}
                    pagination={{ clickable: true }}
                    navigation={{
                        prevEl: '.swiper-button-prev',
                        nextEl: '.swiper-button-next'
                    }}
                    spaceBetween={24}
                    slidesPerView={containerWidth === 'contained' ? 2.5 : 1.3}
                    style={{
                        padding: '4px 4px 40px',
                        '--swiper-pagination-color': '#F0D1FF',
                        '--swiper-navigation-color': '#F0D1FF',
                    } as React.CSSProperties}
                    className="custom-swiper"
                >
                    {similarVenues.map((venue) => (
                        <SwiperSlide key={venue.ID}>
                            <HorizontalFeaturedCard
                                venue={venue}
                                venueBookedTimeSlots={venueBookedTimeSlots}
                                attributeIcons={attributeIcons}
                            />
                        </SwiperSlide>
                    ))}
                </Swiper>
                <Box
                    className="swiper-button-prev"
                    sx={{
                        position: 'absolute',
                        left: { xs: 8, md: 16 },
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 2,
                        width: { xs: '40px', md: '48px' },
                        height: { xs: '40px', md: '48px' },
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2), 0 8px 24px rgba(0, 0, 0, 0.15)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(12px)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                            background: 'rgba(255, 255, 255, 0.12)',
                            transform: 'translateY(-50%) scale(1.08)',
                            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.25), 0 12px 32px rgba(0, 0, 0, 0.2)',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                        },
                        '&:active': {
                            transform: 'translateY(-50%) scale(0.95)',
                            background: 'rgba(255, 255, 255, 0.15)',
                        },
                        '&::after': {
                            fontSize: '1.2rem',
                            color: 'rgba(255, 255, 255, 0.8)',
                        }
                    }}
                />
                <Box
                    className="swiper-button-next"
                    sx={{
                        position: 'absolute',
                        right: { xs: 8, md: 16 },
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 2,
                        width: { xs: '40px', md: '48px' },
                        height: { xs: '40px', md: '48px' },
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2), 0 8px 24px rgba(0, 0, 0, 0.15)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(12px)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                            background: 'rgba(255, 255, 255, 0.12)',
                            transform: 'translateY(-50%) scale(1.08)',
                            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.25), 0 12px 32px rgba(0, 0, 0, 0.2)',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                        },
                        '&:active': {
                            transform: 'translateY(-50%) scale(0.95)',
                            background: 'rgba(255, 255, 255, 0.15)',
                        },
                        '&::after': {
                            fontSize: '1.2rem',
                            color: 'rgba(255, 255, 255, 0.8)',
                        }
                    }}
                />
            </Box>
        </Box>
    );
};

export default SimilarVenues;
